import { useRef, useState } from 'react'
import { Button } from '@/ui'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  PRESORTED_MERCHANT_GONE,
  sendPresortedToBookkeeper,
} from '../../adviser-chat.actions'
import { BookkeeperHandOff } from './BookkeeperHandOff'
import { BUTTON_ROW, QuestionHeading } from './cardParts'
import { WidgetCard } from './WidgetCard'
import type { WidgetProps } from './types'

type Stage = 'confirm' | 'forPractice' | 'notSure'

const REASON_HEADING: Record<Exclude<Stage, 'confirm'>, string> = {
  forPractice: 'What makes this a business expense?',
  notSure: 'What do you know about this spending?',
}

const KEPT_PERSONAL = "Yes, it's personal."

export const ConfirmSortedAutomaticallyWidget = ({
  input,
  resolved,
  outcome,
  onOutcome,
}: WidgetProps) => {
  const dispatch = useAppDispatch()
  const merchantKey =
    typeof input.merchantKey === 'string' ? input.merchantKey : ''
  const [stage, setStage] = useState<Stage>(
    input.disputed === true ? 'forPractice' : 'confirm'
  )
  const [saving, setSaving] = useState(false)
  // State lags a render, so two fast presses would both pass a check on `saving`.
  const sendingRef = useRef(false)

  if (!merchantKey || outcome !== undefined) return null

  const send = async (note: string) => {
    if (sendingRef.current) return
    sendingRef.current = true
    setSaving(true)
    const result = await dispatch(sendPresortedToBookkeeper(merchantKey, note))
    sendingRef.current = false
    setSaving(false)
    if (!result) return
    if (result === PRESORTED_MERCHANT_GONE) {
      onOutcome(
        'Heard no longer has it marked personal on its own, so there was nothing left to send.',
        { label: 'Already settled' }
      )
      return
    }
    onOutcome(
      result.unsureCount > 0
        ? "I've sent it to a bookkeeper with my reason."
        : 'Nothing was sent: those transactions can no longer be changed.',
      result.unsureCount > 0 ? { label: 'Sent to a bookkeeper' } : undefined
    )
  }

  if (stage !== 'confirm') {
    return (
      <WidgetCard>
        <QuestionHeading subtitle="A bookkeeper will review it with your reason">
          {REASON_HEADING[stage]}
        </QuestionHeading>
        <BookkeeperHandOff
          disabled={resolved}
          saving={saving}
          prompt=""
          placeholder="Tell me what it was for"
          helper=""
          onSend={(note) => void send(note)}
          onBack={() => setStage('confirm')}
        />
      </WidgetCard>
    )
  }

  return (
    <WidgetCard>
      <QuestionHeading subtitle="Your answer decides how it is recorded">
        Does that sound right?
      </QuestionHeading>
      <div className={BUTTON_ROW}>
        <Button
          size="sm"
          variant="secondary"
          disabled={resolved}
          onClick={() => onOutcome(KEPT_PERSONAL, { label: 'Kept personal' })}
        >
          {"Yes, it's personal"}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={resolved}
          onClick={() => setStage('forPractice')}
        >
          {"No, it's for the practice"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={resolved}
          onClick={() => setStage('notSure')}
        >
          {"I'm not sure"}
        </Button>
      </div>
    </WidgetCard>
  )
}
